import { onMounted, onUnmounted, nextTick } from 'vue'
import { translationService } from '../services/translation'

const waitForDOMUpdate = () => {
  return new Promise(resolve => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        resolve()
      })
    })
  })
}

export function usePageTranslation(componentRef, selector = '.min-h-screen.bg-white') {
  let isTranslating = false
  let currentTranslationPromise = null

  const translateComponent = async () => {
    if (translationService.getLanguage() === 'en' || isTranslating) {
      return currentTranslationPromise || Promise.resolve()
    }
    
    isTranslating = true
    
    const translationPromise = (async () => {
      try {
        await nextTick()
        await waitForDOMUpdate()
        
        const element = componentRef.value || document.querySelector(selector)
        if (element) {
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Translation timeout')), 30000)
          })
          
          await Promise.race([
            translationService.translateElement(element),
            timeoutPromise
          ])
          
          await nextTick()
          await waitForDOMUpdate()
        }
      } catch (error) {
        console.error('Translation error in usePageTranslation:', error)
      } finally {
        isTranslating = false
        currentTranslationPromise = null
      }
    })()
    
    currentTranslationPromise = translationPromise
    return translationPromise
  }

  const handleLanguageChange = async () => {
    if (isTranslating && currentTranslationPromise) {
      await currentTranslationPromise.catch(() => {})
    }
    await translateComponent()
  }

  onMounted(() => {
    window.addEventListener('language-changed', handleLanguageChange)
    const currentLang = translationService.getLanguage()
    if (currentLang !== 'en') {
      nextTick().then(() => {
        waitForDOMUpdate().then(() => {
          translateComponent()
        })
      })
    }
  })

  onUnmounted(() => {
    window.removeEventListener('language-changed', handleLanguageChange)
  })

  return {
    translateComponent,
    handleLanguageChange
  }
}

